<template>
  <section class="features" id="products">
    <div class="container">
      <div class="section-header">
        <h2 class="section-title">{{ $t('features.title') }}</h2>
        <p class="section-subtitle">{{ $t('features.subtitle') }}</p>
      </div>
      
      <div class="features-grid">
        <div 
          class="feature-card" 
          v-for="(feature, index) in features" 
          :key="index" 
          @click="openDrawer(feature)"
        >
          <div class="card-header">
            <div class="card-icon">
              <!-- 简约线条图标：直接内联 SVG 保证显示 -->
              <svg v-if="feature.iconKey === 'skillithPlatform'" class="feature-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7v10l10 5 10-5V7L12 2z"/><path d="M12 12v5"/></svg>
              <svg v-else-if="feature.iconKey === 'positiveSumGame'" class="feature-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="7" r="2.5"/><circle cx="15" cy="7" r="2.5"/><path d="M5 21v-2a4 4 0 0 1 4-4h2M19 21v-2a4 4 0 0 0-4-4h-2"/></svg>
              <svg v-else-if="feature.iconKey === 'blockchain'" class="feature-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><path d="M10 7h4M10 17h4M7 10v4M17 10v4"/></svg>
              <svg v-else-if="feature.iconKey === 'security'" class="feature-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L3 6v6c0 5.5 9 10 9 10s9-4.5 9-10V6l-9-4z"/><path d="M9 12l2 2 4-4"/></svg>
              <svg v-else-if="feature.iconKey === 'value'" class="feature-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.5 6.5L21 9l-5 4.5 1.5 6.5L12 17l-5.5 3 1.5-6.5L3 9l6.5-.5L12 2z"/></svg>
              <svg v-else class="feature-svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            </div>
            <div class="card-meta">
              <h3 class="card-title">{{ feature.title }}</h3>
              <span class="view-hint">{{ $t('featuresSection.clickHint') }}</span>
            </div>
          </div>
          <p class="card-description">{{ feature.description }}</p>
        </div>
      </div>
    </div>

    <!-- Drawer for detailed information -->
    <DrawerPanel
      :isVisible="showDrawer"
      :feature="selectedFeature"
      @close="closeDrawer"
    />
  </section>
</template>

<script>
import DrawerPanel from './DrawerPanel.vue'

export default {
  name: 'FeaturesSection',
  components: {
    DrawerPanel
  },
  data() {
    return {
      showDrawer: false,
      selectedFeature: null,
      featureKeys: [
        { key: 'skillithPlatform', funcKeys: ['skillithFunc', 'skillithFunc2', 'skillithFunc3', 'skillithFunc4'] },
        { key: 'positiveSumGame', funcKeys: ['positiveSumGameFunc1', 'positiveSumGameFunc2', 'positiveSumGameFunc3', 'positiveSumGameFunc4'] },
        { key: 'blockchain', funcKeys: ['blockchainFunc', 'blockchainFunc2', 'blockchainFunc3', 'blockchainFunc4'] },
        { key: 'security', funcKeys: ['securityFunc1', 'securityFunc2', 'securityFunc3', 'securityFunc4'] },
        { key: 'value', funcKeys: ['assessment', 'record', 'allocation', 'valueFunc4'] },
        { key: 'crossStar', funcKeys: ['crossStarFunc1', 'crossStarFunc2', 'crossStarFunc3', 'crossStarFunc4'] }
      ]
    }
  },
  computed: {
    features() {
      const t = this.$t
      return this.featureKeys.map(({ key, funcKeys }) => ({
        title: t(`features.${key}`),
        description: t(`features.${key}Desc`),
        iconKey: key,
        functionalities: funcKeys.map(fk => t(`features.${fk}`)),
        technicalAdvantage: t(`features.${key}Advantage`),
        marketValue: t(`features.${key}Value`),
        developmentProspect: t(`features.${key}Prospect`)
      }))
    }
  },
  methods: {
    openDrawer(feature) {
      this.selectedFeature = feature
      this.showDrawer = true
    },
    closeDrawer() {
      this.showDrawer = false
      this.selectedFeature = null
    }
  }
}
</script>

<style scoped>
.features {
  padding: 6rem 0;
  background: linear-gradient(180deg, #020617 0%, #0B1220 100%);
  position: relative;
}

.features::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 185, 255, 0.1), transparent);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #F9FAFB;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.2rem;
  color: rgba(209, 213, 219, 0.85);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
}

.feature-card {
  background: rgba(11, 18, 32, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 2rem;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  overflow: hidden;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 100% 0%, rgba(0, 185, 255, 0.05), transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.feature-card:hover {
  transform: translateY(-4px);
  background: rgba(15, 23, 42, 0.8);
  border-color: rgba(0, 185, 255, 0.2);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}

.feature-card:hover::before {
  opacity: 1;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
}

.card-icon {
  width: 48px;
  height: 48px;
  background: rgba(0, 185, 255, 0.08);
  border: 1px solid rgba(0, 185, 255, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00B9FF;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.card-icon .feature-svg-icon {
  width: 26px;
  height: 26px;
  color: #00B9FF;
  flex-shrink: 0;
}

.feature-card:hover .card-icon .feature-svg-icon {
  color: #00D9FF;
}

.feature-card:hover .card-icon {
  background: rgba(0, 185, 255, 0.12);
  border-color: rgba(0, 185, 255, 0.3);
  color: #00D9FF;
}

.card-meta {
  flex: 1;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #F9FAFB;
  margin: 0;
  margin-bottom: 0.4rem;
  line-height: 1.4;
}

.view-hint {
  font-size: 0.75rem;
  color: rgba(0, 185, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.feature-card:hover .view-hint {
  opacity: 1;
}

.card-description {
  font-size: 0.95rem;
  color: rgba(209, 213, 219, 0.8);
  line-height: 1.6;
  margin: 0;
}

@media (max-width: 768px) {
  .features {
    padding: 4rem 0;
  }

  .section-title {
    font-size: 2rem;
  }

  .section-subtitle {
    font-size: 1rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }

  .feature-card {
    padding: 1.5rem;
  }

  .container {
    padding: 0 1.5rem;
  }
}
</style>